import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Compass, ArrowLeft, Loader2 } from "lucide-react";

const formatError = (detail) => {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  return String(detail);
};

const AdminLogin = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex">
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1444090542259-0af8fa96557e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" data-testid="login-back-home" className="text-xs uppercase tracking-[0.3em] flex items-center gap-2 opacity-80 hover:opacity-100">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div>
            <Compass size={28} className="mb-6 opacity-90" />
            <h2 className="font-serif-display text-5xl leading-tight max-w-md">
              The keys to your gallery, in your hands.
            </h2>
            <p className="mt-6 text-sm max-w-sm opacity-80 leading-relaxed">
              Upload, curate, and update your travel photographs and brand from one private space.
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.4em] opacity-60">
            Shaurya Travels · Admin
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-16 bg-[var(--st-bg)]">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden text-xs uppercase tracking-[0.3em] mb-8 inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back
          </Link>
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--st-accent)]">— Admin</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 mb-10">Sign in</h1>
          <form onSubmit={onSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">Email</label>
              <input
                type="email"
                required
                data-testid="admin-email-input"
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">Password</label>
              <input
                type="password"
                required
                data-testid="admin-password-input"
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 px-4 py-3 border border-red-200">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="btn-accent w-full py-4 text-xs uppercase tracking-[0.3em] inline-flex items-center justify-center gap-3 disabled:opacity-60"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? "Signing in…" : "Enter Admin"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
